/**
 * @file
 * A generic wrapper around all the "show lists of data" tables. Most tables at
 * BassMaster simply:
 *  1. Fetch data
 *  2. Process the data
 *  3. Render the data as rows within a <Table/> component
 *  4. Show a message if no data is available
 *  5. Sometimes the table auto-refreshes its data on a timer
 *
 *  This component aims to make that approach as generic as possible
 */
import * as React from 'react';

// Utils
import * as api from '../../util/api';

// Components
import Loader from '../Loader/Loader';
import Message from '../Message/Message';
import Table from '../Table/Table';

// Types
import { RefreshRate } from '../../types';

// Styles
import './api-table.css';

interface IProps {
  // Every ApiTable needs to hit an endpoint
  apiUri: string;
  // Process the results of the api
  apiProcessor: (data: any, limit?: number) => any[];
  // Limit, in query or in processing results
  limit?: number;
  // Table row formatter
  tableRowFormatter: (row: any, index: number) => any; // @TODO: revisit
  // Refresh rate, if needed
  refreshRate: RefreshRate;
  // Class to wrap everything
  rootCssClass?: string;
  // Optional message to show when no data returns
  messageNoResults?: string;
  // Hide/show the header in the Table component
  showHeader?: boolean;
  // Striped row styles
  smartStyles?: boolean;
}
interface IState {
  // The raw, api-returned data
  data?: any[];
  // Simple loading indicator
  loading: boolean;
}
// Data we can optionally send to child component custom table
type ICustomApiTableTable = Pick<IState, 'data'>;

export class ApiTable extends React.PureComponent<IProps, IState> {
  public static defaultProps = {
    // By default, all api calls just pull retrieve JSON
    apiProcessor: (data: any) => data,
    refreshRate: 0,
    showHeader: true,
    tableRowFormatter: (row: object) => row,
  };

  public state = {
    data: [],
    loading: true,
  };

  /**
   * Every API Table must fetch API data then process it
   */
  public fetchData = () => (apiData: any) => {
    const { apiProcessor, limit } = this.props;

    // Make sure we have any data before sending off to process it
    const processedData = apiData ? apiProcessor(apiData, limit) : null;

    return this.setState({
      data: !!processedData ? processedData : [],
      loading: false,
    });
  };

  public async componentDidMount() {
    const { apiUri, refreshRate } = this.props;

    // Polling request
    if (!!refreshRate) {
      await api.subscribe({
        callback: this.fetchData(),
        rate: refreshRate,
        uri: apiUri,
      });
    }
    // Single request
    else {
      await api.get({
        callback: this.fetchData(),
        uri: apiUri,
      });
    }
  }

  public render() {
    // ApiTable offers a totally custom Table that is given the processed data
    const customTable = React.Children.map(this.props.children, child =>
      React.cloneElement(child as React.ReactElement<ICustomApiTableTable>, {
        data: this.state.data,
      })
    );

    // The generic, consistent Table that most components use
    const vanillaTable = (
      <Table
        showHeader={this.props.showHeader}
        smartStyles={this.props.smartStyles}
        tableData={this.state.data.map(this.props.tableRowFormatter)}
      />
    );

    // The existence of children indicates we're using a custom table
    const ActualTable = this.props.children ? customTable : vanillaTable;

    return (
      <div
        className={`api-table ${
          this.props.rootCssClass ? this.props.rootCssClass : ''
        }`}
        data-testid="api-table"
      >
        <Loader loading={this.state.loading} />

        {!this.state.loading && this.state.data.length ? ActualTable : null}

        <Message
          message={this.props.messageNoResults}
          customClass="api-table__no-results-message"
          shouldShow={!this.state.loading && !this.state.data.length}
        />
      </div>
    );
  }
}

export default ApiTable;
